<script setup lang="ts">
import { computed, ref, watch, onMounted, onUnmounted } from 'vue';
import { RouterLink, RouterView, useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import ToastContainer from '@/components/ToastContainer.vue';

/**
 * Header, footer and shell live here rather than in three separate components:
 * they have exactly one caller each, so splitting them would add files without
 * adding reuse.
 */
const auth = useAuthStore();
const route = useRoute();
const router = useRouter();

const menuOpen = ref(false);
const isLocationOpen = ref(false);
const locationDropdownRef = ref<HTMLElement | null>(null);

const handleLocationClickOutside = (e: MouseEvent) => {
  if (locationDropdownRef.value && !locationDropdownRef.value.contains(e.target as Node)) {
    isLocationOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleLocationClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleLocationClickOutside);
});

const publicLinks = [
  { name: 'home', label: 'Beranda' },
  { name: 'catalog', label: 'Katalog' },
  { name: 'articles', label: 'Artikel' },
] as const;

const adminLinks = [
  { name: 'admin', label: 'Ringkasan' },
  { name: 'admin-books', label: 'Katalog' },
  { name: 'admin-rentals', label: 'Transaksi' },
  { name: 'admin-customers', label: 'Pelanggan' },
  { name: 'admin-master', label: 'Master Data' },
  { name: 'admin-articles', label: 'Artikel' },
  { name: 'admin-analytics', label: 'Dashboard' },

] as const;

const initials = computed(() =>
  (auth.user?.name ?? '')
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase(),
);

// Close the mobile menu on navigation, otherwise it covers the new page.
watch(() => route.fullPath, () => {
  menuOpen.value = false;
  isLocationOpen.value = false;
});

const signOut = async (): Promise<void> => {
  await auth.signOut();
  await router.push({ name: 'home' });
};
</script>

<template>
  <!--
    Skip link: the first thing a keyboard or screen reader user reaches, letting
    them jump past the nav straight to the page content.
  -->
  <a
    href="#main-content"
    class="sr-only-focusable absolute top-2 left-2 z-50 rounded-md bg-brand-700 px-4 py-2 text-white"
  >
    Lompat ke konten utama
  </a>

  <div class="flex min-h-screen flex-col">
    <ToastContainer />
    <header class="sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
      <div class="mx-auto flex max-w-6xl items-center gap-2 sm:gap-4 px-3 sm:px-4 py-2.5 sm:py-3">
        <RouterLink
          :to="{ name: 'home' }"
          class="flex items-center gap-2 text-lg font-semibold text-brand-800 shrink-0"
        >
          <img
            src="@/assets/logo.jpg"
            alt="Noorway Book Rent"
            class="h-7 sm:h-9 w-auto object-contain"
          />
        </RouterLink>

        <!-- Button Lokasi -->
        <div class="relative shrink min-w-0" ref="locationDropdownRef">
          <button
            type="button"
            id="location-btn"
            class="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-100 focus:outline-none focus:ring-2 focus:ring-brand-500 sm:text-sm cursor-pointer"
            :aria-expanded="isLocationOpen"
            aria-haspopup="true"
            @click="isLocationOpen = !isLocationOpen"
          >
            <svg
              class="h-3.5 w-3.5 sm:h-4 sm:w-4 text-brand-700 shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span class="max-w-[85px] xs:max-w-[110px] truncate sm:max-w-[160px]">
              Banjarmasin
            </span>
            <svg
              class="h-3 w-3 sm:h-3.5 sm:w-3.5 text-ink-400 transition-transform duration-200 shrink-0"
              :class="{ 'rotate-180': isLocationOpen }"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          <!-- Mobile Backdrop -->
          <div
            v-if="isLocationOpen"
            class="fixed inset-0 z-40 bg-black/25 backdrop-blur-xs sm:hidden"
            aria-hidden="true"
            @click="isLocationOpen = false"
          />

          <!-- Dropdown Popover -->
          <transition
            enter-active-class="transition duration-150 ease-out"
            enter-from-class="transform scale-95 opacity-0"
            enter-to-class="transform scale-100 opacity-100"
            leave-active-class="transition duration-100 ease-in"
            leave-from-class="transform scale-100 opacity-100"
            leave-to-class="transform scale-95 opacity-0"
          >
            <div
              v-if="isLocationOpen"
              class="fixed inset-x-3 top-16 z-50 max-w-sm mx-auto sm:mx-0 sm:absolute sm:inset-x-auto sm:top-full sm:left-0 sm:right-auto sm:mt-2 sm:w-80 origin-top-left rounded-2xl border border-ink-200 bg-white p-3 shadow-xl ring-1 ring-black/5 focus:outline-none space-y-2.5"
              role="menu"
            >
              <div class="px-1 border-b border-ink-100 pb-2">
                <p class="text-xs font-bold uppercase tracking-wider text-ink-400">Lokasi Perpustakaan</p>
                <p class="text-sm font-bold text-ink-900 mt-0.5">Banjarmasin</p>
              </div>

              <!-- Location & Address Detail -->
              <div class="p-2.5 rounded-xl bg-ink-50 border border-ink-200 space-y-1 text-xs text-ink-600">
                <p class="font-semibold text-sm text-ink-900 flex items-center gap-1.5">
                  <span>📍</span>
                  <span>Wash’O Carwash</span>
                </p>
                <p class="leading-relaxed text-ink-600">
                  Jl Perdagangan No.32, Kayutangi. Kota Banjarmasin.
                </p>
                <p class="text-[11px] text-ink-500 pt-0.5">
                  Jam operasional: 08:00 - 20:00 WITA
                </p>
              </div>

              <!-- Google Maps Link -->
              <a
                href="https://maps.app.goo.gl/KaK2XWbfiyF6128V9"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 w-full rounded-lg bg-brand-700 px-3 py-2 text-xs font-semibold text-white shadow-xs hover:bg-brand-800 transition"
              >
                <svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                Buka di Google Maps
              </a>
            </div>
          </transition>
        </div>

        <!-- Primary navigation -->
        <nav aria-label="Main" class="ml-auto hidden items-center gap-1 md:flex">
          <template v-if="auth.isAdmin">
            <RouterLink
              v-for="link in adminLinks"
              :key="link.name"
              :to="{ name: link.name }"
              class="rounded-md px-3 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              active-class="bg-brand-50 text-brand-800"
            >
              {{ link.label }}
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink
              v-for="link in publicLinks"
              :key="link.name"
              :to="{ name: link.name }"
              class="rounded-md px-3 py-2 text-sm font-medium text-ink-600 hover:bg-ink-100 hover:text-ink-900"
              active-class="bg-brand-50 text-brand-800"
            >
              {{ link.label }}
            </RouterLink>
          </template>
        </nav>

        <div class="ml-auto flex items-center gap-2 md:ml-0 shrink-0">
          <template v-if="auth.isAuthenticated">
            <!--
              The avatar is the customer's way into their profile, which is where
              their rentals live. Admins have no profile page, so theirs stays a
              plain badge.
            -->
            <RouterLink
              v-if="auth.isCustomer"
              :to="{ name: 'profile' }"
              class="hidden h-9 w-9 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800 hover:bg-brand-200 md:grid"
              :title="`${auth.user?.name} — profil dan peminjaman saya`"
              active-class="ring-2 ring-brand-600"
            >
              <span aria-hidden="true">{{ initials }}</span>
              <span class="sr-only">Profil dan peminjaman saya</span>
            </RouterLink>
            <span
              v-else
              class="hidden h-9 w-9 place-items-center rounded-full bg-brand-100 text-xs font-semibold text-brand-800 sm:grid"
              :title="auth.user?.name"
            >
              {{ initials }}
            </span>
            <button
              type="button"
              class="hidden rounded-md border border-ink-300 px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100 md:block"
              @click="signOut"
            >
              Keluar
            </button>
          </template>
          <template v-else>
            <!-- Desktop only: Masuk & Daftar (on mobile they are inside the burger menu) -->
            <RouterLink
              :to="{ name: 'login' }"
              class="hidden md:inline-flex rounded-md px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
            >
              Masuk
            </RouterLink>
            <RouterLink
              :to="{ name: 'register' }"
              class="hidden md:inline-flex rounded-md bg-brand-700 px-3 py-2 text-sm font-semibold text-white hover:bg-brand-800"
            >
              Daftar
            </RouterLink>
          </template>

          <button
            type="button"
            class="rounded-md border border-ink-300 p-2 text-ink-700 hover:bg-ink-100 md:hidden"
            :aria-expanded="menuOpen"
            aria-controls="mobile-nav"
            aria-label="Buka atau tutup navigasi"
            @click="menuOpen = !menuOpen"
          >
            <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path
                fill-rule="evenodd"
                d="M3 5h14a1 1 0 100-2H3a1 1 0 000 2zm0 6h14a1 1 0 100-2H3a1 1 0 000 2zm0 6h14a1 1 0 100-2H3a1 1 0 000 2z"
                clip-rule="evenodd"
              />
            </svg>
          </button>
        </div>
      </div>

      <nav
        v-show="menuOpen"
        id="mobile-nav"
        aria-label="Mobile"
        class="border-t border-ink-200 bg-white px-4 py-3 md:hidden space-y-2"
      >
        <div v-if="auth.isAuthenticated" class="pb-2 border-b border-ink-100 flex items-center gap-2.5">
          <div class="h-8 w-8 rounded-full bg-brand-100 text-xs font-bold text-brand-800 grid place-items-center shrink-0">
            {{ initials }}
          </div>
          <div class="min-w-0">
            <p class="text-xs font-bold text-ink-900 truncate">{{ auth.user?.name }}</p>
            <p class="text-[11px] text-ink-500 truncate">{{ auth.user?.email || auth.user?.role }}</p>
          </div>
        </div>

        <div class="space-y-1">
          <template v-if="auth.isAdmin">
            <RouterLink
              v-for="link in adminLinks"
              :key="link.name"
              :to="{ name: link.name }"
              class="block rounded-md px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              active-class="bg-brand-50 text-brand-800 font-semibold"
            >
              {{ link.label }}
            </RouterLink>
          </template>
          <template v-else>
            <RouterLink
              v-for="link in publicLinks"
              :key="link.name"
              :to="{ name: link.name }"
              class="block rounded-md px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              active-class="bg-brand-50 text-brand-800 font-semibold"
            >
              {{ link.label }}
            </RouterLink>
            <RouterLink
              v-if="auth.isCustomer"
              :to="{ name: 'profile' }"
              class="block rounded-md px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              active-class="bg-brand-50 text-brand-800 font-semibold"
            >
              Profil &amp; peminjaman saya
            </RouterLink>
          </template>
        </div>

        <!-- Auth action buttons for mobile: Masuk & Daftar or Keluar -->
        <div v-if="auth.isAuthenticated" class="pt-2 border-t border-ink-100">
          <button
            type="button"
            class="block w-full rounded-md border border-ink-300 px-3 py-2 text-center text-sm font-medium text-ink-700 hover:bg-ink-100"
            @click="signOut"
          >
            Keluar
          </button>
        </div>
        <div v-else class="pt-2 border-t border-ink-100 flex flex-col gap-2">
          <RouterLink
            :to="{ name: 'login' }"
            class="block w-full rounded-md border border-ink-300 px-3 py-2.5 text-center text-sm font-semibold text-ink-700 hover:bg-ink-100"
          >
            Masuk
          </RouterLink>
          <RouterLink
            :to="{ name: 'register' }"
            class="block w-full rounded-md bg-brand-700 px-3 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-brand-800"
          >
            Daftar
          </RouterLink>
        </div>
      </nav>
    </header>


    <main id="main-content" class="mx-auto w-full max-w-6xl flex-1 px-4 py-8">
      <RouterView />
    </main>

    <footer class="border-t border-ink-200 bg-white">
      <div
        class="mx-auto flex max-w-6xl flex-col gap-2 px-4 py-6 text-sm text-ink-500 sm:flex-row sm:items-center sm:justify-between"
      >
        <p>&copy; 2026 Noorways Books. Baca Lebih Banyak, Bayar Lebih Hemat</p>
        <p>Layanan Pelanggan WhatsApp 085151069303</p>
      </div>
    </footer>
  </div>
</template>
